import { Trans } from 'react-i18next';
import { Select } from '@design-system';
import { Box, Divider, Grid, MenuItem } from '@mui/material';

import DialogLayout from 'components/DialogLayout';
import DialogButtons from 'components/Inventories/Stock/SupplyModal/Buttons';

import { useTypeOfProductModal } from './useTypeOfProductModal';
import { useTypeOfProductModalStyles } from './styles';
import { TypeOfProductModalProps } from './types';

/**
 * Modal component for adding product types to selected products
 * Allows users to assign product types for better sales categorization
 *
 * @param {TypeOfProductModalProps} props - Component properties
 * @returns {React.ReactElement} TypeOfProductModal component
 *
 * @example
 * <TypeOfProductModal
 *   isOpen={true}
 *   modalTitle="Add Product Type"
 *   productNumber={3}
 *   typesOfProducts={productTypes}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 * />
 */
function TypeOfProductModal({
  isOpen,
  isDisabled = false,
  modalTitle,
  onCancel,
  onSubmit,
  productNumber,
  typeOfProduct,
  handleTypeOfProduct,
  typesOfProducts,
  isLoading,
}: TypeOfProductModalProps) {
  const styles = useTypeOfProductModalStyles();
  const { t, getDescriptionKey, selectLabel, selectPlaceholder, submitLabel } = useTypeOfProductModal();

  return (
    <DialogLayout open={isOpen}>
      <DialogLayout.Content classes={styles.contentModal}>
        <Box display="flex" justifyContent="center">
          <Box>
            <Box className={styles.title} mb="8px">
              {modalTitle}
            </Box>
            <Box className={styles.subTitle}>
              <Trans i18nKey={getDescriptionKey(productNumber)} values={{ productNumber }} />
            </Box>
            <Box className={styles.description}>
              <Trans i18nKey="menuMaker:typeOfProduct.modal.description1c" />
            </Box>
            <Box className={styles.description}>
              <Trans i18nKey="menuMaker:typeOfProduct.modal.description2" />
            </Box>
          </Box>
        </Box>
        <Divider light />
        {!isLoading && typesOfProducts && typesOfProducts.length > 0 && (
          <Grid className={styles.selectContainer} container justifyContent="center">
            <Grid item xs={6}>
              <Select
                displayEmpty
                fullWidth
                label={selectLabel}
                onChange={(e) => handleTypeOfProduct(e.target.value as string)}
                value={typeOfProduct}
              >
                <MenuItem value="">{selectPlaceholder}</MenuItem>
                {typesOfProducts.map((type) => (
                  <MenuItem key={type.uuid} value={type.uuid}>
                    {t(type.name)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        )}
      </DialogLayout.Content>
      <DialogLayout.Actions>
        <DialogButtons isDisabled={isDisabled} onCancel={onCancel} onSubmit={onSubmit} onSubmitLabel={submitLabel} />
      </DialogLayout.Actions>
    </DialogLayout>
  );
}

export default TypeOfProductModal;
